// Every way an operation can fail, as one hierarchy.
//
// An operation called from code cannot report a problem by printing it and ending the process, so each
// failure is a class with a stable `code` a caller can branch on. The message here states the fact and
// nothing more — the advice that goes with it ("run `flowrunner login`") belongs to whoever is talking to a
// human, which is `cli/fail.ts`. A library consumer gets the fact and writes their own sentence.

export const ERROR_CODES = [
  'FR_NOT_LOGGED_IN',
  'FR_NO_WORKSPACE',
  'FR_NO_SERVICES_DIR',
  'FR_NO_SERVICES',
  'FR_SELECTION_REQUIRED',
  'FR_UNKNOWN_SERVICE',
  'FR_TOKEN_EXPIRED',
  'FR_DEPLOY_FAILED',
  'FR_LOGIN_TIMEOUT',
  'FR_LOGIN_FAILED',
  'FR_PROMPT_REQUIRED',
  'FR_PROMPT_ABORTED',
  'FR_TEMPLATE_NOT_FOUND',
  'FR_UNKNOWN_SERVER',
  'FR_NOT_A_PROJECT',
  'FR_AI_DOCS_MISSING',
  'FR_HTTP',
] as const

export type CliErrorCode = typeof ERROR_CODES[number]

/**
 * The base every failure below extends, so a caller can catch the whole family at once.
 *
 * `name` is assigned from the constructor rather than left to the class, because the bundler is free to
 * rename classes and `error.name` is the one thing that tends to end up in someone's log.
 */
export class CliError extends Error {
  readonly code: CliErrorCode

  constructor(code: CliErrorCode, message: string, name = 'CliError') {
    super(message)

    this.code = code
    this.name = name
  }
}

// ─── Auth ────────────────────────────────────────────────────────────────────

/** No `flowrunner.json` naming a server, or no token in `.flowrunner/`. */
export class NotLoggedInError extends CliError {
  constructor(message = 'This project is not connected to a Flowrunner server.') {
    super('FR_NOT_LOGGED_IN', message, 'NotLoggedInError')
  }
}

/** Logged in, but `flowrunner.json` names no workspace — so there is nowhere to deploy to. */
export class NoWorkspaceError extends CliError {
  constructor(message = 'This project has a login but no workspace attached.') {
    super('FR_NO_WORKSPACE', message, 'NoWorkspaceError')
  }
}

/** The server rejected the deploy token. */
export class TokenExpiredError extends CliError {
  constructor(message = 'The stored deploy token is no longer valid.') {
    super('FR_TOKEN_EXPIRED', message, 'TokenExpiredError')
  }
}

export class LoginTimeoutError extends CliError {
  constructor(message = 'Login timed out. Please try again.') {
    super('FR_LOGIN_TIMEOUT', message, 'LoginTimeoutError')
  }
}

export class LoginFailedError extends CliError {
  constructor(message: string) {
    super('FR_LOGIN_FAILED', message, 'LoginFailedError')
  }
}

// ─── Deploy ──────────────────────────────────────────────────────────────────

/** The project has no `services/` directory at all. */
export class NoServicesDirectoryError extends CliError {
  constructor(message = 'This project has no services/ directory.') {
    super('FR_NO_SERVICES_DIR', message, 'NoServicesDirectoryError')
  }
}

/**
 * `services/` exists but nothing in it loaded as a service.
 *
 * Carries the folders that were skipped and why: with the whole deploy refusing to run, the reason a
 * particular folder was not counted is the only thing that tells anyone what to fix.
 */
export class NoServicesError extends CliError {
  readonly skipped: SkippedService[]

  constructor(skipped: SkippedService[] = [], message = 'No deployable services were found under services/.') {
    super('FR_NO_SERVICES', message, 'NoServicesError')

    this.skipped = skipped
  }
}

export interface SkippedService {
  serviceId: string
  reason: string
}

/**
 * More than one service is deployable and none was chosen.
 *
 * A deploy now touches only the services it names, so "all of them" has to be asked for rather than assumed
 * — silently deploying every service would be the old behaviour wearing a new coat.
 */
export class SelectionRequiredError extends CliError {
  readonly available: string[]

  constructor(available: string[], message = 'This project has more than one service, so the deploy needs to know which.') {
    super('FR_SELECTION_REQUIRED', message, 'SelectionRequiredError')

    this.available = available
  }
}

/** A `-s` name that matches no deployable service in this project. */
export class UnknownServiceError extends CliError {
  readonly serviceId: string
  readonly available: string[]

  constructor(serviceId: string, available: string[]) {
    super(
      'FR_UNKNOWN_SERVICE',
      `Unknown service "${ serviceId }". Available: ${ available.join(', ') }`,
      'UnknownServiceError'
    )

    this.serviceId = serviceId
    this.available = available
  }
}

/** The server accepted the request and refused the package. */
export class DeployFailedError extends CliError {
  readonly status?: number
  readonly body?: string

  constructor(message: string, { status, body }: { status?: number; body?: string } = {}) {
    super('FR_DEPLOY_FAILED', message, 'DeployFailedError')

    this.status = status
    this.body = body
  }
}

// ─── Templates and answers ───────────────────────────────────────────────────

export class TemplateNotFoundError extends CliError {
  readonly templateId: string
  readonly available: string[]

  constructor(templateId: string, available: string[]) {
    super(
      'FR_TEMPLATE_NOT_FOUND',
      `Unknown template "${ templateId }". Available: ${ available.join(', ') }`,
      'TemplateNotFoundError'
    )

    this.templateId = templateId
    this.available = available
  }
}

/**
 * An answer is needed and there is no way to ask for it.
 *
 * The programmatic path throws this instead of blocking on a terminal that may not exist; supply the answer
 * up front, or pass the hook that knows how to ask.
 */
export class PromptRequiredError extends CliError {
  readonly promptName: string

  constructor(promptName: string, message: string) {
    super('FR_PROMPT_REQUIRED', message, 'PromptRequiredError')

    this.promptName = promptName
  }
}

/** The user was asked and declined — Ctrl-C, Escape, or an answer that failed its own validator. */
export class PromptAbortedError extends CliError {
  constructor(message: string) {
    super('FR_PROMPT_ABORTED', message, 'PromptAbortedError')
  }
}

/** Ctrl-C or Escape out of an interactive list. Its own type so a caller can offer something else instead. */
export class SelectionAbortedError extends PromptAbortedError {
  constructor(message = 'Cancelled.') {
    super(message)

    this.name = 'SelectionAbortedError'
  }
}

// ─── Project ─────────────────────────────────────────────────────────────────

/** There is no `flowrunner.json` here, so there is no project to act on. */
export class NotAProjectError extends CliError {
  constructor(message = 'There is no flowrunner.json here, so this is not a Flowrunner project.') {
    super('FR_NOT_A_PROJECT', message, 'NotAProjectError')
  }
}

/** A server name that is not one of the known clusters, and not a URL either. */
export class UnknownServerError extends CliError {
  readonly target: string
  readonly available: string[]

  constructor(target: string, available: string[]) {
    super(
      'FR_UNKNOWN_SERVER',
      `Unknown server "${ target }". Available: ${ available.join(', ') }`,
      'UnknownServerError'
    )

    this.target = target
    this.available = available
  }
}

// ─── AI docs ─────────────────────────────────────────────────────────────────

/**
 * The installed CLI ships no AI docs for the requested provider.
 *
 * They are build output packed into the package, so this means an install that predates them — or a repo
 * checkout that has not been built. Carries the directory looked for, because "which path did you expect"
 * is the only question anyone asks next.
 */
export class AiDocsMissingError extends CliError {
  readonly directory: string

  constructor(directory: string, message = `The installed flowrunner-cli ships no AI docs at ${ directory }.`) {
    super('FR_AI_DOCS_MISSING', message, 'AiDocsMissingError')

    this.directory = directory
  }
}

// ─── Transport ───────────────────────────────────────────────────────────────

/**
 * A non-2xx response, with the status kept as a number.
 *
 * The status is the whole point: deciding "expired token" by looking for "401" inside a message is a test
 * that passes for any server that happens to mention the number.
 *
 * `what` names the call for a human ("Token exchange", "Deploy"); without it the message names the URL.
 */
export class HttpError extends CliError {
  readonly status: number
  readonly body: string
  readonly url: string

  constructor(status: number, body: string, url: string, what?: string) {
    super('FR_HTTP', `${ what ?? `Request to ${ url }` } failed (${ status }): ${ body }`, 'HttpError')

    this.status = status
    this.body = body
    this.url = url
  }
}
